import React from 'react'

import {
  ItemSelector,
  type ItemSelectorProps,
} from '~/components/elements/ItemSelector'
import { type BrandGroup as BrandGroupBase } from '~/gql/graphql'
import { type Unassigned, unassignedSymbol } from '~/utils'

type BrandGroup = App.PickGraphQL<BrandGroupBase, 'id' | 'name'>

export type BrandGroupSelectorProps<TBrandGroup extends BrandGroup> = Omit<
  ItemSelectorProps,
  'data' | 'dataItemKey' | 'onChange' | 'textField' | 'value'
> & {
  brandGroups: TBrandGroup[]
  isNullSelectable?: boolean
  nullText?: string
  onChange?: (brandGroupId: TBrandGroup['id']) => void
  unsetText?: string
  value: TBrandGroup['id'] | Unassigned | null
}

export const BrandGroupSelector = React.forwardRef(
  <TBrandGroup extends BrandGroup>(
    {
      brandGroups,
      isNullSelectable = false,
      nullText,
      onChange,
      unsetText,
      value: valueProp,
      ...rest
    }: BrandGroupSelectorProps<TBrandGroup>,
    ref: React.ForwardedRef<HTMLSpanElement>,
  ) => {
    const data = React.useMemo(
      () =>
        isNullSelectable
          ? [{ id: null, name: nullText }, ...brandGroups]
          : brandGroups,
      [brandGroups, isNullSelectable, nullText],
    )
    const value = React.useMemo(
      () =>
        data.find(({ id }) => id === valueProp) ?? {
          id: unassignedSymbol,
          name: unsetText,
        },
      [data, unsetText, valueProp],
    )

    const handleChange = React.useCallback<EH<ItemSelectorProps, 'onChange'>>(
      (brandGroup: TBrandGroup) => {
        onChange?.(brandGroup.id)
      },
      [onChange],
    )

    return (
      <ItemSelector
        ref={ref}
        data={data}
        dataItemKey="id"
        textField="name"
        value={value}
        onChange={handleChange}
        {...rest}
      />
    )
  },
)
